/**
 * Org-Memberships repository (ADR 0016, Phase B Stufe 1).
 *
 * Reads the M:N user↔org table introduced in migration
 * `20260504000003_org_memberships.sql`. In this phase the table is
 * **read-only** from the frontend — write paths (add/remove
 * memberships, change roles) come in Phase 3 with the admin UI.
 *
 * The active-org context that the rest of the app consumes still
 * comes from `profiles.organization_id` until Phase 2 swaps RLS to
 * use the JWT custom claim. Memberships exist alongside that to
 * surface the "you also have access to …" picker without breaking
 * the legacy single-org assumption.
 */

import { supabase } from '@/integrations/supabase/client';
import { unwrap } from '@/lib/supabase-helpers';

export interface OrgMembership {
  id: string;
  user_id: string;
  organization_id: string;
  role: 'admin' | 'therapeut' | 'assistenz';
  is_default: boolean;
  joined_at: string;
}

export interface OrgMembershipWithOrg extends OrgMembership {
  organization: {
    id: string;
    name: string;
    setting_type?: 'praxis' | 'klinik' | null;
  };
}

export async function listMyMemberships(): Promise<OrgMembershipWithOrg[]> {
  // Returns memberships joined with the organization row so the
  // switcher can render names without a second round-trip.
  const { data, error } = await (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    supabase.from('org_memberships' as any) as any
  )
    .select(
      `id, user_id, organization_id, role, is_default, joined_at,
       organization:organizations!inner(id, name, setting_type)`,
    )
    .order('is_default', { ascending: false })
    .order('joined_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as OrgMembershipWithOrg[];
}

/**
 * Lightweight count for badge rendering. Cheaper than fetching the
 * full list when the UI only needs to know "show switcher? yes/no".
 */
export async function countMyMemberships(): Promise<number> {
  return unwrap(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (supabase.from('org_memberships' as any) as any)
      .select('id', { count: 'exact', head: true }),
  ) as unknown as Promise<number>;
}
